import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	addWorkOrder,
	addWorkOrderExternalData,
	addWorkOrders,
	clearWorkOrderExternalData,
	deleteWorkOrderFromState,
	deleteWorkOrders,
	dropWorkOrdersQueryVariables,
	mapWorkOrder,
	mapWorkOrders,
	openNewWorkOrderFromOutside,
	setWorkOrder,
	setWorkOrderForEditing,
	setWorkOrders,
	setWorkOrderSelectedCalendarDate,
	setWorkOrdersTableConfig,
	startLoadingWorkOrders,
	storeWorkOrdersQueryVariables,
	updateWorkOrder,
	updateWorkOrders,
	upsertWorkOrder,
	upsertWorkOrders,
	workOrdersFailedToLoad,
	workOrdersLoaded,
} from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { DocumentNode } from 'graphql';
import { WorkOrderExportUrlQuery } from '@app/pages/cmms/graphql/work-order.queries';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { ICmmsWorkOrderQueryVariables } from '@app/domain/gql/types';

export interface IWorkOrderState extends EntityState<IWorkOrder> {
	// additional entity state properties
	workOrderEdit: IWorkOrder | null;
	workOrderPagination: IDjangoPaginationData | null;
	workOrderAuPagination: IAuPaginationData | null;
	workOrderQueryVariables: ICmmsWorkOrderQueryVariables | null;
	workOrderIsLoading: boolean;
	workOrderData?: Partial<IWorkOrder>;
	isExportable: boolean;
	tableColumnsConfig: ICMMSColumn[] | null;
	exportUrlQuery: DocumentNode | null;
	selectedCalendarDate: string | null;
}

export function selectWorkOrderPK(item: IWorkOrder): string {
	return item.pk;
}

export const workOrderAdapter: EntityAdapter<IWorkOrder> =
	createEntityAdapter<IWorkOrder>({
		selectId: selectWorkOrderPK,
	});

export const initialState: IWorkOrderState = workOrderAdapter.getInitialState({
	// additional entity state properties
	workOrderEdit: null,
	workOrderPagination: null,
	workOrderIsLoading: true,
	workOrderAuPagination: null,
	workOrderQueryVariables: null,
	workOrderData: null,
	isExportable: false,
	tableColumnsConfig: null,
	exportUrlQuery: WorkOrderExportUrlQuery,
	selectedCalendarDate: null,
});

export const workOrderReducer = createReducer(
	initialState,
	on(addWorkOrder, (state, { workOrder }) => {
		return workOrderAdapter.setAll([workOrder, ...selectAll(state)], state);
	}),
	on(setWorkOrder, (state, { workOrder }) => {
		return workOrderAdapter.setOne(workOrder, state);
	}),
	on(upsertWorkOrder, (state, { workOrder }) => {
		return workOrderAdapter.upsertOne(workOrder, state);
	}),
	on(addWorkOrders, (state, { workOrders }) => {
		return workOrderAdapter.addMany(workOrders, state);
	}),
	on(upsertWorkOrders, (state, { workOrders }) => {
		return workOrderAdapter.upsertMany(workOrders, state);
	}),
	on(updateWorkOrder, (state, { update }) => {
		return workOrderAdapter.updateOne(update, {
			...state,
			workOrderEdit: update.changes as IWorkOrder,
		});
	}),
	on(updateWorkOrders, (state, { updates }) => {
		return workOrderAdapter.updateMany(updates, state);
	}),
	on(mapWorkOrder, (state, { entityMap }) => {
		return workOrderAdapter.mapOne(entityMap, state);
	}),
	on(mapWorkOrders, (state, { entityMap }) => {
		return workOrderAdapter.map(entityMap, state);
	}),
	on(deleteWorkOrderFromState, (state, { pk }) => {
		return workOrderAdapter.removeOne(pk, state);
	}),
	on(deleteWorkOrders, (state, { pks }) => {
		return workOrderAdapter.removeMany(pks, state);
	}),
	on(startLoadingWorkOrders, (state, { variables }) => {
		return {
			...state,
			workOrderAuPagination: {
				first: variables?.first,
				last: variables?.last,
				after: variables?.after,
				before: variables?.before,
			},
			workOrderIsLoading: true,
		};
	}),
	on(workOrdersLoaded, (state, { workOrders, workOrderPagination }) => {
		return workOrderAdapter.setAll(workOrders, {
			...state,
			workOrderPagination: workOrderPagination,
			workOrderIsLoading: false,
		});
	}),
	on(workOrdersFailedToLoad, state => {
		return workOrderAdapter.setAll([], {
			...state,
			workOrderIsLoading: false,
		});
	}),
	on(setWorkOrderForEditing, (state, { workOrderEdit }) => {
		return { ...state, workOrderEdit };
	}),
	on(setWorkOrders, (state, { workOrders }) => {
		return workOrderAdapter.setMany(workOrders, state);
	}),
	on(openNewWorkOrderFromOutside, (state, { workOrderData }) => {
		return { ...state, workOrderData };
	}),
	on(storeWorkOrdersQueryVariables, (state, { variables }) => {
		return { ...state, workOrderQueryVariables: variables };
	}),
	on(dropWorkOrdersQueryVariables, state => {
		return { ...state, workOrderQueryVariables: null };
	}),
	on(setWorkOrdersTableConfig, (state, { isExportable, columns }) => {
		return {
			...state,
			isExportable,
			tableColumnsConfig: columns,
		};
	}),
	on(setWorkOrderSelectedCalendarDate, (state, { selectedDate }) => {
		return {
			...state,
			selectedCalendarDate: selectedDate,
		};
	}),
	on(addWorkOrderExternalData, (state, { workOrderData }) => {
		return {
			...state,
			workOrderData,
		};
	}),
	on(clearWorkOrderExternalData, state => {
		return {
			...state,
			workOrderData: null,
		};
	})
);

const { selectIds, selectEntities, selectAll, selectTotal } =
	workOrderAdapter.getSelectors();

export const workOrderEntitySelector = {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
};
